/**
 * Thin wrapper over the Web Speech API for new-order announcements.
 *
 * Voice availability is the painful part:
 *   - iOS 17+ ships an `uz-UZ` voice (Madina). Pick that when present.
 *   - Most desktops + Android don't have an Uzbek voice at all and the
 *     browser would otherwise read Latin Uzbek with an English accent.
 *     For Latin Uzbek we fall back to a **Turkish** voice (phonetically
 *     close — shares vowel set and most consonants); for Cyrillic Uzbek
 *     we fall back to **Russian** (same script).
 *   - If even those are missing, we let the browser pick its default.
 *
 * We also dedupe identical utterances inside a short window so the
 * persistent alarm doesn't queue an unbounded backlog when playback stalls.
 */

import type { Language } from '@/stores/uiStore';

const BCP47: Record<Language, string> = {
  'uz-Latn': 'uz-UZ',
  'uz-Cyrl': 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

const FALLBACK_LANGS: Record<Language, string[]> = {
  // Turkish first — Latin script + close phonology. Then Azerbaijani as a
  // last regional fallback, then English so something speaks.
  'uz-Latn': ['tr', 'az', 'en'],
  'uz-Cyrl': ['ru', 'en'],
  ru: ['en'],
  en: [],
};

let cachedVoices: SpeechSynthesisVoice[] = [];
let lastSpoken: { text: string; at: number } | null = null;
const DEDUPE_MS = 1500;

function loadVoices() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  cachedVoices = window.speechSynthesis.getVoices();
}

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  loadVoices();
  // The list usually starts empty and is populated after a short delay
  // (Chrome especially). `voiceschanged` fires when it's ready.
  window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
}

function pickVoice(lang: Language): SpeechSynthesisVoice | undefined {
  if (cachedVoices.length === 0) loadVoices();
  const want = BCP47[lang].toLowerCase();
  // Exact lang-region match first (e.g. uz-UZ).
  let v = cachedVoices.find((vv) => vv.lang.toLowerCase() === want);
  if (v) return v;
  // Base language match (e.g. uz).
  const base = want.split('-')[0];
  v = cachedVoices.find((vv) => vv.lang.toLowerCase().startsWith(base + '-'));
  if (v) return v;
  // Falls back: tr / az / ru / en depending on requested lang.
  for (const f of FALLBACK_LANGS[lang]) {
    v = cachedVoices.find((vv) => vv.lang.toLowerCase().startsWith(f + '-'));
    if (v) return v;
    v = cachedVoices.find((vv) => vv.lang.toLowerCase() === f);
    if (v) return v;
  }
  return undefined;
}

export function isVoiceSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function speak(text: string, lang: Language, volume = 1): void {
  if (!isVoiceSupported()) return;
  const now = Date.now();
  if (lastSpoken && lastSpoken.text === text && now - lastSpoken.at < DEDUPE_MS) {
    return;
  }
  lastSpoken = { text, at: now };

  try {
    // Cancel anything mid-flight so the latest tick wins.
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = pickVoice(lang);
    if (voice) {
      u.voice = voice;
      u.lang = voice.lang;
    } else {
      u.lang = BCP47[lang] ?? 'en-US';
    }
    u.rate = 1.0;
    u.pitch = 1.0;
    u.volume = Math.max(0, Math.min(1, volume));
    window.speechSynthesis.speak(u);
  } catch {
    /* unsupported or blocked — silent */
  }
}
